import { Inject, Injectable } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { LoadingIndicatorService, LocalStoreService } from '@frontend/vanilla/core';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { flatten } from 'lodash-es';
import { EventDetailsService } from 'packages/sports/web/app/src/event-details-common/event-details.service';
import { UiManagerActions } from 'packages/sports/web/app/src/ui-manager/ui-manager.actions';
import { uiStateSelector } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { catchError, concatMap, filter, finalize, from, map, mergeMap, startWith, switchMap, tap, timer, withLatestFrom } from 'rxjs';

import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { V1PickId, V2OptionMarketPickId } from '../../../core/picks/pick-id';
import { IDigitalBetslipMessages } from '../../../digital/services/betslip-message-provider.service';
import { EditBetActions } from '../../edit-bet/actions';
import { IEditMyBetState } from '../../edit-bet/state';
import BetslipPickActions from '../../picks/actions';
import BetslipPickFactory from '../../picks/factory/betslip-pick.factory';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import BetslipSystemBetActions from '../../system-bet/actions';
import { LineSwitcherActions } from '../actions';
import { LineSwitcherDialogService } from '../services/line-switcher-dialog.service';
import { LineSwitcherSwapOptionsService } from '../services/line-switcher-swap-options.service';
import { LineSwitcherUtils } from '../services/line-switcher-utils.service';

@Injectable()
export class LineSwitcherEffects implements OnInitEffects {
    private static readonly HIGHLIGHT_DELAY_MS: number = 4000;
    private static readonly STORAGE_KEY = 'lineSwitcherHelpClosed';
    private readonly logger: SportsRemoteLogger;
    private messages: IDigitalBetslipMessages;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private lineSwitcherDialogService: LineSwitcherDialogService,
        private utils: LineSwitcherUtils,
        loggerFactory: LoggerFactory,
        private localStorage: LocalStoreService,
        private eventDetailsService: EventDetailsService,
        private loadingIndicator: LoadingIndicatorService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) private messageProvider: IBetslipMessagesProviderService,
        private betslipPickUtilsService: BetslipPickUtilsService,
        private pickFactory: BetslipPickFactory,
        private lineSwitcherSwapOptions: LineSwitcherSwapOptionsService,
        private bettingOfferConfig: BettingOfferConfig,
    ) {
        this.logger = loggerFactory.getLogger('LineSwitcherEpic');
        this.messages = this.messageProvider.getMessages() as IDigitalBetslipMessages;
    }

    ngrxOnInitEffects(): Action {
        return LineSwitcherActions.effectInitialized();
    }

    handleInitLineSwitcherEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.initLineSwitcher),
            map((action) => (BetslipV1Pick.isPick(action.pick) || BetslipV2OptionMarketPick.isPick(action.pick) ? action.pick : undefined)),
            filter(isDefined),
            map((pick) => LineSwitcherActions.openSportsLineSwitcher({ pick })),
        );
    });

    handleOpenSportsLineSwitcherEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.openSportsLineSwitcher),
            withLatestFrom(this.store.select(uiStateSelector), this.store.select(betslipSelector)),
            switchMap(([action, uiState, betslipState]) => {
                const sportPick = LineSwitcherUtils.convertToSportPick(action.pick);
                const loading = this.loadingIndicator.start();

                return from(this.eventDetailsService.getEvent(sportPick.eventId, false, this.bettingOfferConfig.includeLinkedFixtures)).pipe(
                    withLatestFrom(this.utils.getPossibleWinnings(betslipState)),
                    concatMap(([event, winnings]) => {
                        const inColumn = uiState.states.some((s) => s === UiStates.RightColumnVisible);
                        if (!event) {
                            this.lineSwitcherDialogService.openModalDialog(inColumn);

                            return [
                                ...this.handleError(`Event not found; Event ID: ${sportPick.eventId}`, this.messages.lineSwitcher.GeneralError),
                                UiManagerActions.addState({ state: UiStates.LineSwitcher }),
                            ];
                        }

                        const optionGroup = this.lineSwitcherSwapOptions.getOptionGroups(event, action.pick.market.id);

                        if (!optionGroup) {
                            this.lineSwitcherDialogService.openModalDialog(inColumn);

                            return [
                                ...this.handleError(
                                    `Event option group not found; Event ID: ${sportPick.eventId}`,
                                    this.messages.lineSwitcher.AllMarketsClosedError,
                                ),
                                UiManagerActions.addState({ state: UiStates.LineSwitcher }),
                            ];
                        }

                        this.lineSwitcherDialogService.openModalDialog(inColumn, optionGroup, event, sportPick);

                        const actions: Action[] = [
                            LineSwitcherActions.updateSwapButtonState({
                                payload: {
                                    title: this.messages.lineSwitcher.AcceptButton,
                                    isDisabled: true,
                                },
                            }),
                            LineSwitcherActions.updateOptionGroup({ payload: optionGroup }),
                            UiManagerActions.addState({ state: UiStates.LineSwitcher }),
                        ];

                        if (winnings) {
                            actions.push(LineSwitcherActions.updatePossibleWinningsState({ payload: winnings }));
                        }

                        return actions;
                    }),
                    finalize(() => loading.done()),
                    catchError((error: Error) => {
                        const inColumn = uiState.states.some((s) => s === UiStates.RightColumnVisible);
                        this.lineSwitcherDialogService.openModalDialog(inColumn);

                        return [...this.handleError(error && error.message), UiManagerActions.addState({ state: UiStates.LineSwitcher })];
                    }),
                );
            }),
        );
    });

    handleAddToSwapSelectionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.addToSwapSelection),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            switchMap(([_, betslipState]) => {
                const effects = this;

                return this.utils.getPossibleWinnings(betslipState).pipe(
                    filter((winnings) => !!winnings),
                    concatMap(function* (winnings) {
                        yield LineSwitcherActions.updatePossibleWinningsState({ payload: winnings });
                        const newPickId = betslipState.lineSwitcher.swapPickState.newPickId;
                        if (newPickId && effects.isPickAlreadySelected(betslipState, newPickId)) {
                            yield LineSwitcherActions.updateSwapButtonState({
                                payload: {
                                    isDisabled: true,
                                },
                            });
                        }
                    }),
                    catchError((error: Error) => this.handleError(error && error.message)),
                );
            }),
        );
    });

    private isPickAlreadySelected(state: IBetslipState, newPickId: V1PickId | V2OptionMarketPickId): boolean {
        return state.types.base.currentSelectedType === BetslipType.EditBet
            ? this.isPickAlreadySelectedEditBet(state.types.editBet, newPickId)
            : !!state.picks.pickList.find((pick) => pick.id.isEqual(newPickId));
    }

    private isPickAlreadySelectedEditBet(editBetState: IEditMyBetState, newPickId: V1PickId | V2OptionMarketPickId): boolean {
        return (
            !!editBetState.picks.pickList.find((pick) => pick.id.isEqual(newPickId)) ||
            !!editBetState.addPicksState.pickList.find((pick) => pick.id.isEqual(newPickId))
        );
    }

    handlePushUpdateEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.handlePushUpdate),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            switchMap(([action, betslipState]) => {
                return this.utils.getPossibleWinnings(betslipState).pipe(
                    concatMap((winnings) => {
                        const lineSwitcherState = betslipState.lineSwitcher;
                        if (!lineSwitcherState.optionGroup) {
                            return [];
                        }

                        const swapPickState = lineSwitcherState.swapPickState;
                        const { optionId } = swapPickState.newPickId || swapPickState.oldPickId;
                        const option = LineSwitcherUtils.getOption(optionId, lineSwitcherState.optionGroup.options);
                        if (!option) {
                            return [];
                        }

                        if (!winnings) {
                            return [];
                        }

                        const actions: Action[] = [LineSwitcherActions.updatePossibleWinningsState({ payload: winnings })];
                        if (swapPickState.newPickId) {
                            if (!option.online && !lineSwitcherState.swapButtonState.isDisabled) {
                                actions.push(
                                    LineSwitcherActions.updateSwapButtonState({
                                        payload: {
                                            isDisabled: true,
                                        },
                                    }),
                                );
                            } else if (option.online && lineSwitcherState.swapButtonState.isDisabled) {
                                actions.push(
                                    LineSwitcherActions.updateSwapButtonState({
                                        payload: {
                                            isDisabled: false,
                                        },
                                    }),
                                );
                            }
                        }

                        return actions;
                    }),
                    catchError((error) => this.handleError(error?.message)),
                );
            }),
        );
    });

    handleSwapSportsPickEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.swapSportPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            mergeMap(([action, betslipState]) => {
                const lineSwitcherState = betslipState.lineSwitcher;

                const oldPickId = lineSwitcherState.swapPickState.oldPickId;
                const newPickId = lineSwitcherState.swapPickState.newPickId;
                const optionGroup = lineSwitcherState.optionGroup;
                const oldPick = LineSwitcherUtils.getPick(oldPickId, betslipState);

                if (!optionGroup || !oldPick || !newPickId) {
                    return this.handleError(`Pick to switch is not found; Pick ID: ${oldPickId}`);
                }

                return betslipState.types.base.currentSelectedType === BetslipType.EditBet
                    ? this.betslipPickUtilsService.loadPickFromApi(newPickId).pipe(
                          map((enhancedFixture) => {
                              return this.handleSwap(newPickId, oldPickId, oldPick, enhancedFixture.fixture, optionGroup);
                          }),
                          concatMap((newPick) => [
                              EditBetActions.swapPick({ payload: { oldPick, newPick, highlightNewPick: true } }),
                              LineSwitcherActions.closeLineSwitcher(),
                              LineSwitcherActions.trackSwap({ oldPick, newPick }),
                          ]),
                      )
                    : this.betslipPickUtilsService.loadPickFromApi(newPickId).pipe(
                          map((enhancedFixture) => {
                              return this.handleSwap(newPickId, oldPickId, oldPick, enhancedFixture.fixture, optionGroup);
                          }),
                          concatMap((newPick) => {
                              const actionsArray = [
                                  BetslipPickActions.replacePick({ pickId: newPickId, newPick }),
                                  BetslipPickActions.selectPick({ pickId: newPickId }),
                                  LineSwitcherActions.closeLineSwitcher(),
                                  LineSwitcherActions.trackSwap({ oldPick, newPick }),
                              ];
                              if (
                                  betslipState.types.base.currentSelectedType === BetslipType.System &&
                                  betslipState.types.systemBet.picks[oldPickId.toString()].isBanker
                              )
                                  actionsArray.push(BetslipSystemBetActions.toggleSystemBetPickBanker({ pickId: newPickId.toString() }));

                              return actionsArray;
                          }),
                          startWith(
                              BetslipPickActions.replacePick({
                                  pickId: oldPickId,
                                  newPick: new BetslipUnknownPick(newPickId),
                              }),
                          ),
                      );
            }),
        );
    });

    private handleSwap(
        newPickId: V1PickId | V2OptionMarketPickId,
        oldPickId: any,
        oldPick: BetslipPick,
        fixture: Fixture,
        optionGroup: EventOptionGroup,
    ): BetslipPick {
        const newPick = this.pickFactory.createPick(newPickId, oldPick.tracking, fixture, undefined, undefined, undefined, oldPick.groupInfo);
        if (!newPick) {
            throw new Error(`Pick to switch is not found; Pick ID: ${oldPickId}`);
        }

        newPick.acceptedPrice.isManuallyAccepted = oldPick.acceptedPrice.isManuallyAccepted;

        const {
            option: { id },
        } = newPick as BetslipV1Pick | BetslipV2OptionMarketPick;

        const option = LineSwitcherUtils.getOption(id, [...optionGroup.options, ...flatten(optionGroup.subGroups?.map((g) => g.options))]);

        if (!option) {
            throw new Error(`Pick to switch is not found; Pick ID: ${oldPickId}`);
        }
        newPick.tracking.betDetails = LineSwitcherUtils.getBetDetails(optionGroup, option);

        return newPick;
    }

    handleCloseLineSwitcherEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.closeLineSwitcher),
            switchMap(() =>
                timer(LineSwitcherEffects.HIGHLIGHT_DELAY_MS).pipe(
                    withLatestFrom(this.store.select(uiStateSelector)),
                    switchMap(([action, uiState]) => {
                        const uiContainsLineSwitcher = uiState.states.some((s) => s === UiStates.LineSwitcher);

                        return uiContainsLineSwitcher ? [] : [LineSwitcherActions.removePickHighlight()];
                    }),
                    startWith(UiManagerActions.removeState({ state: UiStates.LineSwitcher })),
                ),
            ),
        );
    });

    handleCancelLineSwitcherEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.cancelLineSwitcher),
            map(() => UiManagerActions.removeState({ state: UiStates.LineSwitcher })),
        );
    });

    handleLineSwitcherErrorsEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.updateErrorState),
                tap((action) => this.logger.error(action.payload.logMessage)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    // Page refresh
    handleEffectInitialized$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LineSwitcherActions.effectInitialized),
            concatLatestFrom(() => this.store.select(uiStateSelector)),
            filter(([_, uiState]) => uiState.states.some((state) => state === UiStates.LineSwitcher)),
            map(() => LineSwitcherActions.closeLineSwitcher()),
        );
    });

    // Page refresh
    closeLineSwitcher$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestAddPick, BetslipPickActions.requestAddBetBuilderPick, BetslipPickActions.removePick),
            concatLatestFrom(() => this.store.select(uiStateSelector)),
            filter(([_, uiState]) => uiState.states.some((state) => state === UiStates.LineSwitcher)),
            map(() => LineSwitcherActions.closeLineSwitcher()),
        );
    });

    handleCloseHelpBoxEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.closeHelpMessageBox),
                tap(() => this.localStorage.set(LineSwitcherEffects.STORAGE_KEY, true)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private handleError(logMessage: string = 'Unknown error', message: string = this.messages.lineSwitcher.GeneralError): Action[] {
        return [
            LineSwitcherActions.updateErrorState({
                payload: {
                    message,
                    logMessage,
                },
            }),
            LineSwitcherActions.updateSwapButtonState({
                payload: {
                    isDisabled: false,
                    title: this.messages.lineSwitcher.BackButton,
                },
            }),
        ];
    }
}
